import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import OpenPlaysLink from "@/components/OpenPlaysLink";
import RentalCaption from "@/components/RentalCaption";
import VenueRentalNotice from "@/components/VenueRentalNotice";
import NextSevenDaysStrip from "@/components/NextSevenDaysStrip";
import CourtLayoutDialog from "@/components/CourtLayoutDialog";
import { ArrowLeft, Map } from "lucide-react";
import { toast } from "sonner";
import { formatSlot } from "@/lib/formatSlot";
import { isSlotPast } from "@/lib/slotTime";

import { API } from "@/lib/apiBase";

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const AllCourtsAvailability = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [courtsDay, setCourtsDay] = useState([]);
  const [loading, setLoading] = useState(false);
  // Multi-court selection: { [courtId]: Set<time_slot> }. Empty object = no
  // selection. Each court manages its own auto-bridge/auto-trim contiguous
  // range, but the bottom action bar sums across every court.
  const [selection, setSelection] = useState({});
  // "View Map" popup state — shows the facility layout so customers can
  // visualise where each court is before picking a slot.
  const [mapOpen, setMapOpen] = useState(false);
  const weekStripRef = useRef(null);

  const fetchDay = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calendar/day?date=${toDateStr(selectedDate)}`);
      setCourtsDay(res.data.courts || []);
    } catch {
      toast.error("Failed to load availability");
      setCourtsDay([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    // Ensure the page always opens at the "Choose View & Rate" heading,
    // not scrolled mid-page (e.g. if the previous route was scrolled down).
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchDay();
    // Clear selection whenever the date changes.
    setSelection({});
  }, [fetchDay]);

  // Explicit handler for user-initiated date picks from the Weekly strip.
  // Scrolls to the strip so the new day's availability table is in view —
  // this is NEVER called on mount, so fresh navigations to /availability
  // always stay at the top ("Choose View & Rate" heading).
  const handleDatePick = (d) => {
    setSelectedDate(d);
    setTimeout(() => {
      weekStripRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 250);
  };

  const jumpTo = (courtId, slot) => {
    // Defensive guard: ignore clicks that bypassed the disabled UI (e.g. on
    // mobile where touch events occasionally fire on disabled buttons).
    if (isSlotPast(toDateStr(selectedDate), slot)) return;
    setSelection((prev) => {
      // Per-court Set of selected slots. Looking up an absent court returns
      // an empty Set — selecting from a brand-new court adds it to the map.
      const prevSlots = prev[courtId] ? new Set(prev[courtId]) : new Set();

      // Ordered list of time slots for THIS court, used to compute the
      // auto-bridge (fill gaps) and auto-trim (deselect everything after)
      // positions. All courts share the same slot grid so we fall back to
      // the reference slots if the per-court list is missing.
      const thisCourt = courtsDay.find((c) => c.court_id === courtId);
      const ordered = (thisCourt?.all_slots || refSlots).map(
        (s) => s.time_slot
      );

      // Helper: rebuild the top-level selection object with the new Set,
      // dropping empty entries so we don't leave dangling court keys.
      const writeBack = (newSet) => {
        const next = { ...prev };
        if (newSet.size === 0) delete next[courtId];
        else next[courtId] = newSet;
        return next;
      };

      // Clicking an already-selected slot trims THIS court's selection to
      // end AT the clicked slot. Other courts' selections are untouched.
      if (prevSlots.has(slot)) {
        if (prevSlots.size === 1) {
          return writeBack(new Set());
        }
        const cutIdx = ordered.indexOf(slot);
        if (cutIdx === -1) {
          const trimmed = new Set(prevSlots);
          trimmed.delete(slot);
          return writeBack(trimmed);
        }
        const trimmed = new Set();
        prevSlots.forEach((t) => {
          const i = ordered.indexOf(t);
          if (i !== -1 && i <= cutIdx) trimmed.add(t);
        });
        return writeBack(trimmed);
      }

      // Selecting a new slot on THIS court: bridge any gap between the
      // clicked slot and the existing per-court selection by auto-picking
      // every AVAILABLE slot in between. Skips booked/blocked slots.
      if (ordered.length === 0) {
        const next = new Set(prevSlots);
        next.add(slot);
        return writeBack(next);
      }

      const newIdx = ordered.indexOf(slot);
      if (newIdx === -1) {
        const next = new Set(prevSlots);
        next.add(slot);
        return writeBack(next);
      }

      const prevIndices = Array.from(prevSlots)
        .map((t) => ordered.indexOf(t))
        .filter((i) => i !== -1);
      const allIndices = [...prevIndices, newIdx];
      const minIdx = Math.min(...allIndices);
      const maxIdx = Math.max(...allIndices);

      const courtSlots = thisCourt?.all_slots || [];
      const bridged = new Set(prevSlots);
      for (let i = minIdx; i <= maxIdx; i++) {
        const s = courtSlots[i];
        if (s && s.status === "available") bridged.add(s.time_slot);
      }
      bridged.add(slot);
      return writeBack(bridged);
    });
  };

  const clearSelection = () => setSelection({});

  // Roll up the multi-court selection into a per-court breakdown + grand
  // total. Each item is a court's slice of the order with sorted slot list.
  const buildSelectionSummary = () => {
    const items = [];
    let totalAmount = 0;
    let totalSlots = 0;
    Object.entries(selection).forEach(([courtId, slotSet]) => {
      if (!slotSet || slotSet.size === 0) return;
      const court = courtsDay.find((c) => c.court_id === courtId);
      if (!court) return;
      const ordered = Array.from(slotSet).sort(
        (a, b) =>
          parseInt(a.split(":")[0], 10) - parseInt(b.split(":")[0], 10)
      );
      const courtTotal = ordered.reduce((sum, sl) => {
        const slot = court.all_slots.find((s) => s.time_slot === sl);
        return sum + (slot?.rate || 0);
      }, 0);
      items.push({
        court_id: courtId,
        court_name: court.court_name,
        time_slots: ordered,
        court_total: courtTotal,
      });
      totalAmount += courtTotal;
      totalSlots += ordered.length;
    });
    // Stable sort by court_name so the action-bar summary doesn't reorder
    // mid-click.
    items.sort((a, b) => a.court_name.localeCompare(b.court_name));
    return { items, totalAmount, totalSlots };
  };

  const summary = buildSelectionSummary();

  const handleContinue = () => {
    if (summary.items.length === 0) return;
    const dateStr = toDateStr(selectedDate);
    // Group-style payload: list of { court_id, time_slots } plus the
    // shared header. Customer info is collected on the Payment page.
    navigate("/payment", {
      state: {
        groupDraft: {
          booking_date: dateStr,
          items: summary.items.map((it) => ({
            court_id: it.court_id,
            court_name: it.court_name,
            time_slots: it.time_slots,
            court_total: it.court_total,
          })),
          total_amount: summary.totalAmount,
          total_slots: summary.totalSlots,
        },
      },
    });
  };

  // The reference slot list drives the Time column of the all-courts
  // grid. Court 5 exposes fewer slots than the standard courts, so we
  // use the court with the longest slot list as the base — that way the
  // 00:00-01:00 "last slot of day" ordering is preserved from the
  // backend without alphabetic re-sorting.
  const refSlots = (() => {
    let best = [];
    courtsDay.forEach((c) => {
      if ((c.all_slots || []).length > best.length) best = c.all_slots;
    });
    return best;
  })();

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="border-b border-white/10 sticky top-0 bg-[#0a0a0a]/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <h1
            className="flex items-center cursor-pointer"
            onClick={() => navigate("/")}
            data-testid="brand-logo-link"
          >
            <img
              src="/assenda-logo.png"
              alt="Assenda"
              className="h-8 sm:h-10 w-auto object-contain"
              data-testid="brand-logo"
            />
          </h1>
          <div className="flex items-center gap-2 sm:gap-3">
            <OpenPlaysLink />
            <button
              onClick={() => navigate("/courts")}
              data-testid="back-to-courts"
              className="text-sm text-[#a1a1aa] hover:text-white flex items-center gap-2"
            >
              <ArrowLeft size={16} /> Back
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Page title */}
        <h1
          data-testid="choose-view-rate-heading"
          className="text-lg sm:text-xl lg:text-2xl font-black tracking-tighter mb-3"
        >
          Book a <span className="text-[#8cc63f]">Court</span>
        </h1>

        {/* COURT RENTAL info banner + MORE INFO trigger */}
        <RentalCaption />

        <div className="grid grid-cols-1 gap-3 items-start">
          {/* Main: week strip + heatmap + table */}
          <div className="space-y-3">
            {/* 7-day strip — slides with the selected date */}
            <div ref={weekStripRef} className="scroll-mt-24">
              <NextSevenDaysStrip
                anchorDate={selectedDate}
                onDateSelect={handleDatePick}
              />
            </div>

            {/* Table — compact: no horizontal scroll, no vertical scroll. */}
            <div className="card-surface p-2 sm:p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm sm:text-base font-bold">
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </h3>
                  <button
                    type="button"
                    data-testid="view-map-button"
                    onClick={() => setMapOpen(true)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-[#8cc63f]/40 bg-[#8cc63f]/10 text-[#8cc63f] hover:bg-[#8cc63f]/20 hover:border-[#8cc63f] transition-colors"
                  >
                    <Map size={12} />
                    View map
                  </button>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-[#a1a1aa]">
                  <LegendDot color="bg-[#121212] border border-white/10" label="Available" />
                  <LegendDot color="bg-yellow-400/15 border border-yellow-400/40" label="Booked" />
                  <LegendDot color="bg-orange-500/20 border border-orange-500/40" label="Blocked" />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-10">
                  <div className="inline-block w-6 h-6 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : courtsDay.length === 0 ? (
                <p className="text-center text-[#a1a1aa] py-8 text-sm">
                  No courts available.
                </p>
              ) : (
                <div className="rounded-sm border border-white/5">
                  <table className="w-full table-fixed text-[9px] sm:text-[11px] border-separate border-spacing-0">
                    <thead>
                      <tr>
                        <th className="bg-[#121212] text-left py-1 px-1 label-text border-b border-white/10 text-[8px] sm:text-[10px] w-[54px] sm:w-[78px]">
                          Time
                        </th>
                        {courtsDay.map((c) => (
                          <th
                            key={c.court_id}
                            className="bg-[#121212] text-center py-1 px-0 sm:px-1 label-text border-b border-white/10 text-[8px] sm:text-[10px]"
                          >
                            {c.court_name.replace(/^Court /, "C")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {refSlots.map((ref) => (
                        <tr key={ref.time_slot}>
                          <td className="bg-[#121212] py-0.5 px-1.5 text-[#a1a1aa] whitespace-nowrap border-b border-white/5 text-[9px] sm:text-[10px]">
                            {formatSlot(ref.time_slot)}
                          </td>
                          {courtsDay.map((c) => {
                            const found = c.all_slots.find(
                              (x) => x.time_slot === ref.time_slot
                            );
                            // Court 5 closes 9 AM – 4 PM and 1 – 7 AM; the
                            // availability payload simply omits those slots.
                            // Render a neutral "—" cell instead of
                            // falling back to Court 1's rate.
                            const isClosed = !found;
                            const cell = found || { status: "closed" };
                            const past = isSlotPast(toDateStr(selectedDate), ref.time_slot);
                            const clickable = !isClosed && cell.status === "available" && !past;
                            const isSelected =
                              clickable &&
                              selection[c.court_id]?.has(ref.time_slot);
                            const base =
                              "block w-full rounded-sm px-0.5 sm:px-1 py-0.5 text-[9px] sm:text-[10px] font-semibold text-center transition-colors truncate";
                            let cls = "";
                            let label = "";
                            if (isClosed) {
                              cls = "bg-[#0a0a0a] border border-white/5 text-[#525252] cursor-not-allowed";
                              label = "—";
                            } else if (past && cell.status === "available") {
                              // Past slots that *would* otherwise be bookable
                              // get a faded "Past" pill — visually distinct
                              // from active "Available" cells, signalling
                              // "can't pick this anymore".
                              cls = "bg-[#0a0a0a] border border-white/5 text-[#525252] cursor-not-allowed";
                              label = "—";
                            } else if (cell.status === "available") {
                              cls = isSelected
                                ? "bg-[#8cc63f] text-black border border-[#8cc63f]"
                                : "bg-[#121212] border border-white/10 text-white hover:border-[#8cc63f] hover:text-[#8cc63f] cursor-pointer";
                              label = `₱${cell.rate}`;
                            } else if (cell.status === "booked") {
                              cls = "bg-yellow-400/15 border border-yellow-400/40 text-yellow-200";
                              label = "Booked";
                            } else if (cell.status === "manually_blocked") {
                              cls = "bg-orange-500/20 border border-orange-500/40 text-orange-300";
                              label = cell.note || "Blocked";
                            } else if (cell.status === "externally_blocked") {
                              cls = "bg-purple-500/20 border border-purple-500/40 text-purple-300";
                              label = cell.note || "Blocked";
                            }
                            return (
                              <td key={c.court_id} className="py-0.5 px-0.5 border-b border-white/5">
                                {clickable ? (
                                  <button
                                    type="button"
                                    data-testid={`all-cell-${c.court_id}-${ref.time_slot}`}
                                    onClick={() => jumpTo(c.court_id, ref.time_slot)}
                                    className={`${base} ${cls}`}
                                  >
                                    {label}
                                  </button>
                                ) : (
                                  <span className={`${base} ${cls} block cursor-default`}>
                                    {label}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <VenueRentalNotice />
      </div>

      {/* Sticky action bar — visible whenever ANY court has a selection */}
      {summary.items.length > 0 && (
        <div
          data-testid="selection-action-bar"
          className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur border-t border-[#8cc63f]/40 shadow-[0_-4px_24px_rgba(140,198,63,0.12)]"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[#a1a1aa]">
                {summary.totalSlots} slot{summary.totalSlots > 1 ? "s" : ""} across{" "}
                <span className="text-[#8cc63f] font-bold">
                  {summary.items.length} court{summary.items.length > 1 ? "s" : ""}
                </span>{" "}
                · {selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
              {/* Per-court breakdown — wraps neatly on narrow screens. */}
              <p
                data-testid="selection-court-breakdown"
                className="text-[11px] text-[#a1a1aa] mt-0.5 truncate"
              >
                {summary.items
                  .map(
                    (it) =>
                      `${it.court_name}: ${it.time_slots.length}h (₱${it.court_total.toLocaleString()})`
                  )
                  .join(" · ")}
              </p>
              <p className="text-lg font-black text-white mt-0.5">
                ₱{summary.totalAmount.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="selection-clear-btn"
                onClick={clearSelection}
                className="px-3 py-2 text-xs font-bold text-[#a1a1aa] border border-white/15 rounded-sm hover:border-white hover:text-white transition-colors"
              >
                Clear
              </button>
              <button
                type="button"
                data-testid="selection-continue-btn"
                onClick={handleContinue}
                className="px-4 py-2 text-xs font-bold bg-[#8cc63f] text-black rounded-sm hover:bg-[#9ed15c] transition-colors"
              >
                Continue to book →
              </button>
            </div>
          </div>
        </div>
      )}

      <CourtLayoutDialog
        open={mapOpen}
        onOpenChange={setMapOpen}
        title="Assenda Court Layout"
        courts={[]}
      />
    </div>
  );
};

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className={`w-2.5 h-2.5 rounded-sm ${color}`}></span>
    {label}
  </span>
);

export default AllCourtsAvailability;
